import { useQuery } from '@tanstack/react-query';
import type { RunSource, RunStatus, RunSummary } from 'fuel-benchmark-contracts';
import { Plus } from 'lucide-react';
import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { orpc } from '../../apis/orpc-query';
import { Button } from '../../components/ui/Button';
import { DataTable, type DataTableColumns } from '../../components/ui/DataTable';
import { InfoCell } from '../../components/ui/InfoCell';
import { Select, type SelectOption } from '../../components/ui/Select';
import { StatusText } from '../../components/ui/StatusText';
import { formatDay } from '../../lib/format';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { formatResult, runConditionsSummary, RUN_SOURCES, RUN_STATUSES, runSourceTone, runStatusTone } from './run-display';

/** The run table is small enough that one page keeps client-side sorting honest (as on the vehicles list). */
const PAGE_SIZE = 100;
const CATALOGUE_PAGE_SIZE = 200;

/**
 * The filters live in the URL, so the coverage matrix can link straight to "this vehicle under this scenario"
 * and the resulting view is shareable and survives a reload.
 */
export const RunsListPage = () => {
  const { t } = useI18nTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const vehicleId = searchParams.get('vehicleId') ?? '';
  const scenarioId = searchParams.get('scenarioId') ?? '';
  const status = searchParams.get('status') ?? '';
  const source = searchParams.get('source') ?? '';
  // Set by the import page's success panel rather than by a filter control: it names one batch, so the only
  // sensible way in is a link, and "clear filters" is the way back out.
  const importBatchId = searchParams.get('importBatchId') ?? '';

  const setFilter = (key: string, value: string): void => {
    const next = new URLSearchParams(searchParams);

    if (value === '') {
      next.delete(key);
    } else {
      next.set(key, value);
    }

    setSearchParams(next, { replace: true });
  };

  const vehicles = useQuery(
    orpc.vehicles.list.queryOptions({ input: { page: 1, pageSize: CATALOGUE_PAGE_SIZE, sortBy: 'code', sortDirection: 'asc' } }),
  );

  const scenarios = useQuery(
    orpc.scenarios.list.queryOptions({ input: { page: 1, pageSize: CATALOGUE_PAGE_SIZE, sortBy: 'code', sortDirection: 'asc' } }),
  );

  const runs = useQuery(
    orpc.runs.list.queryOptions({
      input: {
        page: 1,
        pageSize: PAGE_SIZE,
        sortBy: 'testDate',
        sortDirection: 'desc',
        vehicleId: vehicleId === '' ? undefined : vehicleId,
        scenarioId: scenarioId === '' ? undefined : scenarioId,
        status: status === '' ? undefined : (status as RunStatus),
        source: source === '' ? undefined : (source as RunSource),
        importBatchId: importBatchId === '' ? undefined : importBatchId,
      },
    }),
  );

  const vehicleOptions: SelectOption[] = useMemo(
    () => (vehicles.data?.items ?? []).map((vehicle) => ({ value: vehicle.id, label: `${vehicle.code} — ${vehicle.manufacturer} ${vehicle.model}` })),
    [vehicles.data],
  );

  const scenarioOptions: SelectOption[] = useMemo(
    () => (scenarios.data?.items ?? []).map((scenario) => ({ value: scenario.id, label: scenario.code })),
    [scenarios.data],
  );

  const statusOptions: SelectOption[] = RUN_STATUSES.map((value) => ({ value, label: t(`enums.runStatus.${value}`) }));
  const sourceOptions: SelectOption[] = RUN_SOURCES.map((value) => ({ value, label: t(`enums.runSource.${value}`) }));

  const columns: DataTableColumns<RunSummary> = useMemo(
    () => [
      {
        accessorKey: 'testDate',
        header: t('runs.columns.testDate'),
        cell: ({ row }) => <span className="whitespace-nowrap">{formatDay(row.original.testDate)}</span>,
      },
      {
        id: 'vehicle',
        accessorFn: (run) => run.vehicle.label,
        header: t('runs.columns.vehicle'),
        cell: ({ row }) => (
          <InfoCell
            title={row.original.vehicle.label}
            subline={row.original.vehicle.code}
          />
        ),
      },
      {
        id: 'scenario',
        accessorFn: (run) => run.scenario.code,
        header: t('runs.columns.scenario'),
        cell: ({ row }) => (
          <InfoCell
            title={row.original.scenario.code}
            subline={runConditionsSummary(row.original, t)}
          />
        ),
      },
      {
        id: 'route',
        accessorFn: (run) => run.route?.code ?? '',
        header: t('runs.columns.route'),
        cell: ({ row }) =>
          row.original.route === null ? (
            <span className="text-muted-foreground">{t('common.notSet')}</span>
          ) : (
            <InfoCell
              title={row.original.route.name}
              subline={`${row.original.route.code} v${row.original.route.version}`}
            />
          ),
      },
      {
        id: 'result',
        accessorFn: (run) => run.result?.value ?? null,
        header: t('runs.columns.result'),
        cell: ({ row }) => <span className="whitespace-nowrap font-medium">{formatResult(row.original.result)}</span>,
      },
      {
        accessorKey: 'status',
        header: t('runs.columns.status'),
        cell: ({ row }) => (
          <StatusText
            label={t(`enums.runStatus.${row.original.status}`)}
            tone={runStatusTone(row.original.status)}
          />
        ),
      },
      {
        accessorKey: 'source',
        header: t('runs.columns.source'),
        cell: ({ row }) => (
          <StatusText
            label={t(`enums.runSource.${row.original.source}`)}
            tone={runSourceTone(row.original.source)}
          />
        ),
      },
    ],
    [t],
  );

  const hasFilters = vehicleId !== '' || scenarioId !== '' || status !== '' || source !== '' || importBatchId !== '';

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">{t('runs.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('runs.subtitle')}</p>
        </div>
        <Button onClick={() => navigate('/runs/new')}>
          <Plus className="size-4" />
          {t('runs.new')}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={vehicleId}
          onChange={(event) => setFilter('vehicleId', event.target.value)}
          options={vehicleOptions}
          placeholder={t('runs.allVehicles')}
          aria-label={t('runs.allVehicles')}
          className="w-auto min-w-56"
        />
        <Select
          value={scenarioId}
          onChange={(event) => setFilter('scenarioId', event.target.value)}
          options={scenarioOptions}
          placeholder={t('runs.allScenarios')}
          aria-label={t('runs.allScenarios')}
          className="w-auto min-w-48"
        />
        <Select
          value={status}
          onChange={(event) => setFilter('status', event.target.value)}
          options={statusOptions}
          placeholder={t('runs.allStatuses')}
          aria-label={t('runs.allStatuses')}
          className="w-auto min-w-44"
        />
        <Select
          value={source}
          onChange={(event) => setFilter('source', event.target.value)}
          options={sourceOptions}
          placeholder={t('runs.allSources')}
          aria-label={t('runs.allSources')}
          className="w-auto min-w-40"
        />
        {importBatchId === '' ? null : (
          <StatusText
            label={t('runs.importBatchFilter')}
            tone="info"
          />
        )}
        {hasFilters ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSearchParams(new URLSearchParams(), { replace: true })}
          >
            {t('actions.clearFilters')}
          </Button>
        ) : null}
      </div>

      {runs.isError ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {t('errors.loadFailed')}
        </p>
      ) : null}

      <DataTable
        columns={columns}
        data={runs.data?.items ?? []}
        isLoading={runs.isPending}
        emptyMessage={t('runs.empty')}
        // A draft re-enters the wizard where it was left off; a judged run opens its verdict page.
        onRowClick={(run) => navigate(run.status === 'DRAFT' ? `/runs/new?runId=${run.id}` : `/runs/${run.id}`)}
      />
    </div>
  );
};
